import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Fade, Zoom, Paper, Chip } from '@mui/material';
import { 
  Close as CloseIcon, 
  NavigateNext as NextIcon, 
  NavigateBefore as PrevIcon,
  CheckCircle as CheckIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';

interface TourStep {
  id: string;
  title: string;
  description: string;
  target: string; // CSS selector for the element to highlight
  position: 'top' | 'bottom' | 'left' | 'right';
  highlight?: boolean;
}

interface GuideTourProps {
  isVisible: boolean;
  onClose: () => void;
  user: any;
}

const tourSteps: TourStep[] = [
  {
    id: 'connect-account',
    title: 'Connect Your First Account',
    description: 'Click the "Connect Account" button to start connecting your social media accounts.',
    target: '.guide-connect-button',
    position: 'bottom',
    highlight: true
  },
  {
    id: 'meta-platforms',
    title: 'Connect with Your Social Media',
    description: 'Choose "Connect with Meta" to easily manage Facebook, Instagram, and WhatsApp messages.',
    target: '.guide-connect-button',
    position: 'bottom',
    highlight: true
  },
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    description: 'Once connected, you\'ll see your messages, analytics, and manage everything from here.',
    target: '.guide-dashboard',
    position: 'top',
    highlight: false
  },
  {
    id: 'sidebar-features',
    title: 'Sidebar Features',
    description: 'Use the left sidebar to navigate between Messages, Auto Replies, and Agent Prompts sections.',
    target: '.guide-navigation',
    position: 'right',
    highlight: true
  }
];

const GuideTour: React.FC<GuideTourProps> = ({ isVisible, onClose, user }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setShowTour(true);
        highlightCurrentElement();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  useEffect(() => {
    if (showTour) {
      highlightCurrentElement();
    }
  }, [currentStep, showTour]);

  const highlightCurrentElement = () => {
    const currentTourStep = tourSteps[currentStep];
    if (currentTourStep?.target) {
      // Remove previous highlights
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });

      // Add highlight to current element
      const targetElement = document.querySelector(currentTourStep.target);
      if (targetElement) {
        targetElement.classList.add('tour-highlight');
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    setShowTour(false);
    setCurrentStep(0);
    // Remove all highlights
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
    });
    
    // Mark tour as completed in localStorage
    if (user?.username) {
      const tourShownKey = `tour_shown_${user.username}`;
      localStorage.setItem(tourShownKey, 'true');
    }
    
    onClose();
  };

  const skipTour = () => {
    completeTour();
  };

  if (!isVisible || !showTour) return null;

  const currentTourStep = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  return (
    <>
      {/* Overlay - removed blur and made more transparent */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 9998,
        }}
        onClick={skipTour}
      />

      {/* Tour Card */}
      <Fade in={showTour} timeout={600}>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            maxWidth: 450,
            width: '90%',
            mx: 2,
          }}
        >
          <Zoom in={showTour} timeout={400} style={{ transitionDelay: '200ms' }}>
            <Paper
              elevation={24}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #1877F2 0%, #42A5F5 100%)',
                  p: 3,
                  color: 'white',
                  position: 'relative',
                }}
              >
                {/* Close Button */}
                <Button
                  onClick={skipTour}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    minWidth: 'auto',
                    p: 1,
                    color: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </Button>

                {/* Step Counter */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LightbulbIcon sx={{ mr: 1.5, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {currentTourStep.title}
                  </Typography>
                </Box>

                {/* Progress */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Step {currentStep + 1} of {tourSteps.length}
                  </Typography>
                  <Box sx={{ flex: 1, height: 4, backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: 2 }}>
                    <Box
                      sx={{
                        width: `${((currentStep + 1) / tourSteps.length) * 100}%`,
                        height: '100%',
                        backgroundColor: 'white',
                        borderRadius: 2,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    mb: 4,
                    fontSize: '1.1rem',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {currentTourStep.description}
                </Typography>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    onClick={prevStep}
                    disabled={isFirstStep}
                    startIcon={<PrevIcon />}
                    sx={{
                      color: 'text.secondary',
                      '&:disabled': { opacity: 0.5 },
                    }}
                  >
                    Previous
                  </Button>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      onClick={skipTour}
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(0, 0, 0, 0.2)',
                        color: 'text.secondary',
                        '&:hover': {
                          borderColor: 'rgba(0, 0, 0, 0.3)',
                        },
                      }}
                    >
                      Skip Tour
                    </Button>
                    
                    <Button
                      onClick={nextStep}
                      variant="contained"
                      endIcon={isLastStep ? <CheckIcon /> : <NextIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #1877F2, #42A5F5)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1666cf, #1976d2)',
                        },
                      }}
                    >
                      {isLastStep ? 'Complete' : 'Next'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Zoom>
        </Box>
      </Fade>

      {/* CSS for highlighting elements */}
      <style>
        {`
          .tour-highlight {
            position: relative !important;
            z-index: 9997 !important;
            box-shadow: 0 0 0 3px rgba(24, 119, 242, 0.4) !important;
            border-radius: 6px !important;
            transition: all 0.3s ease !important;
            background: rgba(255, 255, 255, 1) !important;
            backdrop-filter: none !important;
          }
          
          /* Highlight edilen element'in içindeki buton da aydınlık olsun */
          .tour-highlight button {
            background: rgba(255, 255, 255, 1) !important;
            color: #2c3e50 !important;
            border: 1px solid rgba(95, 213, 182, 0.3) !important;
          }
          

          

        `}
      </style>
    </>
  );
};

export default GuideTour;
